import type { ReferenceNames } from "@services/shipments/types";
import { format, parseISO } from "date-fns";

/**
 * Set of property names that represent date fields
 * NOTE: This mirrors DatePropertyNames from backend (services-node/src/shipments/utils/shipmentProperties.ts)
 * Keep these in sync when adding new date fields!
 */
export const DatePropertyNames = new Set<ReferenceNames>([
  "ata",
  "atd",
  "etd",
  "eta",
  "lfd",
  "doc_cut_off_date",
  "port_cut_off_date",
  "empty_return_date",
  "gated_out_date",
  "discharge_date",
  "delivery_appointment_date",
]);

/**
 * Check if a property name is a date field
 * @param propertyName The property name to check
 * @returns true if the property is a date field
 */
export function isDateProperty(propertyName: ReferenceNames): boolean {
  return DatePropertyNames.has(propertyName);
}
/**
 * Format a date string to a readable format using only the YYYY-MM-DD part.
 *
 * Ignores timezone completely. If the string contains a full ISO timestamp,
 * only the YYYY-MM-DD portion is used.
 *
 * @example
 * formatDateValue("2025-01-15") → "Jan 15, 2025"
 * formatDateValue("2025-01-15T10:30:00+05:30") → "Jan 15, 2025"
 * formatDateValue("invalid") → "invalid"
 */
export function formatDateToDisplayIgnoreTZ(dateString: string): string {
  const match = dateString?.match(/^(\d{4}-\d{2}-\d{2})/);
  if (!match) return dateString;

  const dateOnly = parseISO(match[1]); // treated as local date, no time zone shift

  return format(dateOnly, "MMM d");
}
/**
 * Format multiple date values (for properties that can have multiple dates)
 *
 * @param dateStrings Array of date strings
 * @returns Comma-separated formatted dates
 *
 * @example
 * formatDateValues(["2025-01-15", "2025-01-20"]) // "Jan 15, 2025, Jan 20, 2025"
 */
export function formatDateValues(dateStrings: string[]): string {
  return dateStrings
    .map(formatDateToDisplayIgnoreTZ)
    .filter((d) => d !== "") // Remove empty strings
    .join(", ");
}
